<script lang="ts">
  import { onMount } from "svelte";
  import { writable } from "svelte/store";
  import { fade } from "svelte/transition";
  import { Ver } from "$components";
  import type {
    AgreementSpot,
    DebtorAccess,
    DebtorData,
    MyPagesStart,
    Queue,
    QueueApplicant,
    QueueApplicantVolition,
    Tag,
  } from "$apis/parkito";
  import DebtorForm, {
    type DebtorFormOutput,
  } from "$shared/components/parkito/debtors/DebtorForm.svelte";
  import QueueApplicantVolitionSelector from "$shared/components/parkito/queue/QueueApplicantVolitionSelector.svelte";
  import Separator from "$shared/components/Separator.svelte";
  import { CustomButton, ErrorCloud, Label, Toast } from "$components";
  import { withApi } from "$shared/lib/api-parkito";
  import { Form } from "$shared/lib/validation_next";
  import LoadingIndicator from "$shared/components/LoadingIndicator.svelte";

  export let params: { applicantId: string };
  export let myPagesData: SvelteStore<MyPagesStart> | undefined;
  export let refreshMyPagesData: () => Promise<void>;

  let queue: Queue | null = null;
  let queueTags: Tag[] | null = null;

  let debtorForm = Form.input<DebtorFormOutput>();
  let agreementSpotsOfDebtor = writable<AgreementSpot[]>([]);

  const createDebtor = async (debtorData: DebtorData): Promise<string> => {
    const res = await withApi((api) => {
      return api.debtorCreate(debtorData);
    });

    if (!res.isOk()) {
      Toast.error("Parkör kunde ej skapas");
      throw ("Parkör kunde ej skapas");
    }

    return res.value.id;
  };

  const updateDebtor = async (
    debtorId: string,
    debtorData: DebtorData,
  ) => {
    const res = await withApi((api) => {
      return api.debtorPut(debtorId, debtorData);
    });

    if (!res.isOk()) {
      Toast.error("Parkör kunde ej sparas");
      return;
    }
  };

  const submitDebtorApplicant = async (debtorId: string) => {
    // submit the
    const res = await withApi((api) => {
      return api.applicantSetDebtor(params.applicantId, { debtorId });
    });

    if (!res.isOk()) {
      Toast.error("Kunde inte lägga in parkör i kö-ansökan");
      throw "Kunde inte lägga in parkör i kö-ansökan";
    }

    applicant = res.value;
  };

  let applicant: QueueApplicant | null = null;
  let volitionForm = Form.input<QueueApplicantVolition>();
  let applicantAndDebtorForm = Form.object<
    { volition: QueueApplicantVolition; debtor: DebtorFormOutput }
  >({
    volition: volitionForm,
    debtor: debtorForm,
  });

  const getQueue = async (queueId: string) => {
    const res = await withApi((api) => {
      return api.queueGet(queueId);
    });

    if (res.isOk()) {
      queue = res.value;
    } else {
      Toast.error("Kunde inte hämta kö");
    }
  };

  const getTags = async (queueId: string) => {
    const res = await withApi((api) => {
      return api.queueGetAvailability(queueId, {});
    });

    if (res.isOk()) {
      queueTags = res.value.tags.map((s) => s.tag);
    } else {
      Toast.error("Kunde inte hämta kö-taggar");
    }
  };

  const getApplicant = async (applicantId: string) => {
    const res = await withApi((api) => {
      return api.queueGetApplicant(applicantId);
    });

    if (res.isOk()) {
      applicant = res.value;
    } else {
      Toast.error("Kunde inte hämta kö");
    }
  };

  onMount(async () => {
    await getApplicant(params.applicantId);
    if (applicant) {
      await getQueue(applicant.queueId);
      await getTags(applicant.queueId);
    }
  });

  let debtorsList: DebtorAccess[] = [];
  $: {
    debtorsList = [];
    // if applicant has a debtor put it first in the list
    // because that is the one that is selected by default
    // and is also less confusing to the user
    if ($myPagesData?.debtors) {
      for (let debtor of $myPagesData.debtors) {
        if (debtor.debtor.id === applicant?.debtor?.id) {
          debtorsList = [debtor, ...debtorsList];
        } else {
          debtorsList.push(debtor);
        }
      }
    }
  }

  let information_is_refreshed = false;
  $: {
    information_is_refreshed = Boolean(
      applicant && applicant.debtor && !applicant.refresh?.requestSentAt &&
        !applicant.refresh?.requestExpiresAt &&
        !applicant.refresh?.reminderSentAt,
    );
  }

  const submitForm = async () => {
    if (!$applicantAndDebtorForm.res.isOk()) {
      Toast.error("Fel i formulär");
      throw "Fel i formulär";
    }

    const form = $applicantAndDebtorForm.res.value;

    let debtorId: string | null = null;
    if (form.debtor.type === "new") {
      debtorId = await createDebtor(form.debtor.debtor as DebtorData);
    } else {
      debtorId = form.debtor.debtorId;
      if (form.debtor.editDebtor) {
        await updateDebtor(
          form.debtor.debtorId,
          form.debtor.editDebtor as DebtorData,
        );
      }
    }

    await submitDebtorApplicant(debtorId);

    let updateRes = await withApi((api) => {
      return api.queueUpdateApplicant(applicant!.id, {
        volition: form.volition,
      });
    });

    if (updateRes.isOk()) {
      Toast.info("Ny intresseanmälan skapad");
      await refreshMyPagesData();
    } else {
      Toast.error("Kunde inte spara ");
      throw "Kunde inte spara";
    }
  };
</script>

<div in:fade={{ duration: 150 }}>
  <div class="px-[24px] py-[24px] max-mobile:px-[8px] max-mobile:py-[8px]">
    <h2 class="text-[#181818] text-[17px] -mt-[30px] inline relative">
      Uppdatera profil
    </h2>
  </div>

  <Separator customClass="max-mobile:hidden" />

  <div class="p-[24px]">
    {#if !myPagesData && $myPagesData !== undefined}
      <LoadingIndicator />
    {:else if information_is_refreshed}
      <!-- Section: Thanks for profile updating-->
      <h2>
        <img
          src="/icons/checkmark-blue-green.svg"
          alt="OK"
          class="inline w-[18px] mr-[6px] mb-[3px]"
        />Tack för att du uppdaterade din profil!
      </h2>
      <p class="mt-[24px]">
        Vi kommer att kontakta dig så snart vi har en ledig plats att erbjuda.
      </p>
      <p class="mt-[16px]">
        Vänliga hälsningar,<br />
        Parkando
      </p>
      <p class="mt-[24px] underline">
        <a href="#/">Gå till startsidan för Mina sidor</a>
      </p>
    {:else if applicant && queueTags && queue}
      <DebtorForm
        selectableDebtors={debtorsList}
        debtorOutput={debtorForm}
      />
      <Ver gap="m">
        <QueueApplicantVolitionSelector
          context={{
            siteTags: queueTags,
            showResidentalParkingPrices: Boolean(
              applicant.wantsResidentalVatFree,
            ),
            queue: queue,
            agreementSpotsOfDebtor,
          }}
          bind:formOutput={volitionForm}
        />
        {#if !$applicantAndDebtorForm.res.isOk()}
          <Label type="error" text={$applicantAndDebtorForm.res.error} />
        {/if}
        <CustomButton
          onClick={submitForm}
          type="ghost"
          disabled={!$applicantAndDebtorForm.res.isOk()}
        >Spara</CustomButton>
      </Ver>
    {:else}
      <ErrorCloud
        altMessage="no-tags-found"
        title="Ansökning kunde inte hittas"
        text={`Ansökan med id ${params.applicantId} kunde inte hittas is systemet`}
      />
    {/if}
  </div>
</div>
